import { LoadingState } from "@repo/app-types/client/client";
import type { PaymentMethodInfo } from "@repo/app-types/entities/balanceTransaction";
import type { BillingPlan } from "@repo/app-types/entities/company";
import type { AdminApiAiUsage } from "@repo/app-types/entities/creditUsage";
import type { AdminApiLicenseUtilization } from "@repo/app-types/entities/entitlement";
import { getTeammateLicenseTypeForProductKey } from "@repo/app-types/entities/teammate";
import { BillingPlansEnumToDisplayName } from "@repo/app-types/helpers/billingFeatureGating";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { capitalize, Size } from "@repo/ui/helpers/helpers";
import { Link, linkStyle } from "@repo/ui/Link/index";
import { IconChartBar, IconPencil } from "@tabler/icons-react";
import type { FC, ReactNode } from "react";
import { Link as RouterLink } from "react-router";
import { appPaths } from "../../../app/appPaths.js";
import { teammateLicenseCopy } from "../teammateSettings/teammateLicenses.js";
import {
  formatUsageAgainstIncluded,
  formatWholeNumber,
} from "./usageFormatting.js";

interface AiCreditsSummary {
  label: string;
  content: ReactNode;
}

/**
 * The company's credit allowance: the monthly grant, with whatever is left on
 * purchased packs on a line of its own. A contract granting no recurring
 * credits has no allowance to name, so it reports what was consumed instead.
 */
function describeAiCredits(usage: AdminApiAiUsage): AiCreditsSummary {
  const { balances } = usage;
  if (usage.included_ai_credits === null) {
    return {
      label: "Monthly AI credits",
      content: `${formatWholeNumber(usage.credits_used)} used`,
    };
  }
  const included = `${formatWholeNumber(usage.included_ai_credits)} included`;
  return balances.kind === "live" && balances.remaining_purchased_credits > 0
    ? {
        label: "AI credits",
        content: (
          <span className="flex flex-col gap-0.5">
            <span>{`Monthly: ${included}`}</span>
            <span className="text-xs font-normal text-muted-foreground">
              {`Purchased: ${formatWholeNumber(balances.remaining_purchased_credits)} remaining`}
            </span>
          </span>
        ),
      }
    : { label: "Monthly AI credits", content: included };
}

interface PlanOverviewCardProps {
  plan: BillingPlan | null;
  paymentMethod: PaymentMethodInfo | null;
  licenseUtilization?: AdminApiLicenseUtilization | null;
  aiUsage?: AdminApiAiUsage;
  portalLoadingState: LoadingState;
  /** Absent for a company without a billing account. */
  onManagePlan?: () => void;
  onUpdatePaymentMethod: () => void;
}

/** The billing page's plan summary: plan, licenses, and monthly AI credits. */
export const PlanOverviewCard: FC<PlanOverviewCardProps> = (props) => {
  const {
    plan,
    paymentMethod,
    licenseUtilization,
    aiUsage,
    portalLoadingState,
    onManagePlan,
    onUpdatePaymentMethod,
  } = props;
  const aiCredits = aiUsage ? describeAiCredits(aiUsage) : null;

  return (
    <section className="space-y-3">
      <h2 className="text-base font-semibold text-foreground">Your plan</h2>
      <div className="overflow-hidden rounded-md border border-border-light bg-white">
        <div className="flex flex-col justify-between gap-4 px-4 py-3 sm:flex-row sm:items-center">
          <div className="flex min-w-0 items-center gap-2.5">
            <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-indigo-50 text-indigo-700">
              <IconChartBar className="size-4.5" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground">
                {plan
                  ? `${BillingPlansEnumToDisplayName[plan]} plan`
                  : "Alpharun plan"}
              </p>
              {paymentMethod && paymentMethod.is_card ? (
                <div className="group mt-0.5 flex items-center gap-1 text-sm text-muted-foreground">
                  <span>
                    {`${capitalize(paymentMethod.brand)} ending in ${
                      paymentMethod.last4
                    } · Expires ${
                      paymentMethod.exp_month >= 10 ? "" : "0"
                    }${paymentMethod.exp_month}/${paymentMethod.exp_year}`}
                  </span>
                  <div className="hidden group-hover:flex">
                    <IconButton
                      variant={ButtonVariant.Tertiary}
                      icon={<IconPencil className="h-3 w-3" />}
                      onClick={onUpdatePaymentMethod}
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          {onManagePlan ? (
            <Button
              className="shrink-0"
              onClick={onManagePlan}
              variant={ButtonVariant.Secondary}
              size={Size.SMALL}
              isLoading={portalLoadingState === LoadingState.LOADING}
            >
              Manage plan &amp; invoices
            </Button>
          ) : null}
        </div>
        <dl className="grid border-t border-border-light sm:grid-cols-2">
          <div className="px-4 py-3">
            <dt className="flex items-center justify-between gap-2 text-xs font-medium text-muted-foreground">
              <span>Licenses</span>
              {licenseUtilization ? (
                <RouterLink
                  to={appPaths.settingsTeammates.build()}
                  className={linkStyle({
                    className: "shrink-0 text-xs font-medium",
                  })}
                >
                  Manage
                </RouterLink>
              ) : null}
            </dt>
            <dd className="mt-1 text-sm font-semibold text-foreground tabular-nums">
              {licenseUtilization ? (
                <span className="flex flex-col gap-0.5">
                  {licenseUtilization.productUtilization.map((product) => (
                    <span key={product.product_key}>
                      {`${
                        teammateLicenseCopy[
                          getTeammateLicenseTypeForProductKey(
                            product.product_key,
                          )
                        ].label
                      }: ${formatUsageAgainstIncluded(
                        product.used_licenses,
                        product.max_licenses,
                      )}`}
                    </span>
                  ))}
                </span>
              ) : (
                "Not available"
              )}
            </dd>
          </div>
          <div className="border-t border-border-light px-4 py-3 sm:border-t-0 sm:border-l">
            <dt className="text-xs font-medium text-muted-foreground">
              {aiCredits?.label ?? "Monthly AI credits"}
            </dt>
            <dd className="mt-1 text-sm font-semibold text-foreground tabular-nums">
              {aiCredits?.content ?? "—"}
            </dd>
          </div>
        </dl>
        <div className="border-t border-border-light px-4 py-3 text-xs text-muted-foreground">
          {portalLoadingState === LoadingState.ERROR ? (
            <p className="mb-1 text-red-700">
              {"Sorry, an error occurred. Please try again or "}
              <Link href="mailto:support@alpharun.com">
                reach out to our team
              </Link>
              {" for help."}
            </p>
          ) : null}
          <p>
            {`Need help with your plan? `}
            <Link href="mailto:support@alpharun.com">Email us</Link>
            {" or "}
            <Link href="https://cal.frontapp.com/alpharun/paul/7fc09a3a">
              book a meeting
            </Link>
            {"."}
          </p>
        </div>
      </div>
    </section>
  );
};
